import { ref, reactive, computed, readonly } from 'vue'
import { authApi, authStateManager, authUtils } from './auth'
import { apiRequest } from './request'
import { useUserStore } from '@/stores/user'
import { useBrandStore } from '@/stores/brand'
import { startTokenRefreshTimer } from '@/utils/tokenRefreshTimer'

import type { 
  LoginRequest, 
  LoginResult, 
  AuthUserInfo, 
  JwtUserInfo,
  SmsLoginRequest,
  SendSmsCodeRequest,
  BrandInfo
} from './auth'

// 认证状态Hook
export function useAuth() {
  // 使用 user store 和 brand store
  const userStore = useUserStore()
  const brandStore = useBrandStore()
  
  // 响应式状态
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 初始化状态
  const initAuthState = () => {
    userStore.initUserState()
  }

  // 第一步：用户登录（获取临时token）
  const login = async (loginRequest: LoginRequest): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const loginResult = await authUtils.handleLogin(loginRequest)
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 短信验证码登录（获取临时token）
  const loginWithSms = async (smsLoginRequest: SmsLoginRequest): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const loginResult = await authUtils.handleLoginWithSms(smsLoginRequest)
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 第二步：获取用户可访问的品牌列表
  const getMyBrands = async (): Promise<BrandInfo[]> => {
    try {
      const response = await authApi.getMyBrands()
      
      if (!response.success) {
        throw new Error(response.message || '获取品牌列表失败')
      }

      return response.data || []
    } catch (err: any) {
      error.value = err.message
      throw err
    }
  }

  // 第三步：选择品牌（获取正式token）
  const selectBrand = async (brandId: number): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.selectBrand(brandId)
      
      if (!response.success) {
        throw new Error(response.message || '选择品牌失败')
      }

      const loginResult = response.data
      
      // 清除临时token和用户信息
      apiRequest.clearTemporaryToken()
      authStateManager.clearTemporaryUser()
      
      // 设置正式token和refreshToken（存储到localStorage）
      apiRequest.setToken(loginResult.accessToken)
      if (loginResult.refreshToken) {
        apiRequest.setRefreshToken(loginResult.refreshToken)
      }
      
      // 设置正式用户信息（包含完整的角色和权限，存储到localStorage）
      if (loginResult.userInfo) {
        authStateManager.setCurrentUser({
          userId: loginResult.userInfo.userId,
          userName: loginResult.userInfo.userName,
          roles: loginResult.userInfo.roles || [],
          permissions: loginResult.userInfo.permissions || [],
          expiresAt: loginResult.expiresAt
        })
      }

      // 设置当前品牌信息
      // 从之前获取的品牌列表中查找对应的品牌信息
      const brands = await getMyBrands()
      const selectedBrand = brands.find(brand => brand.id === brandId)
      if (selectedBrand) {
        brandStore.setCurrentBrand(selectedBrand)
      } else {
        // 如果找不到品牌信息，创建一个基本的品牌对象
        brandStore.setCurrentBrand({ 
          id: brandId, 
          name: `品牌${brandId}`, 
          brandCode: `BRAND${brandId}`,
          logo: ''
        })
      }
      
      // 启动token定时刷新检查
      startTokenRefreshTimer()
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 切换品牌
  const switchBrand = async (brandId: number): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.switchBrand(brandId)
      
      if (!response.success) {
        throw new Error(response.message || '切换品牌失败')
      }

      const loginResult = response.data
      
      // 清除临时token和用户信息（如果有的话）
      apiRequest.clearTemporaryToken()
      authStateManager.clearTemporaryUser()
      
      // 设置新的正式token和refreshToken（存储到localStorage）
      apiRequest.setToken(loginResult.accessToken)
      if (loginResult.refreshToken) {
        apiRequest.setRefreshToken(loginResult.refreshToken)
      }
      
      // 更新正式用户信息（存储到localStorage）
      if (loginResult.userInfo) {
        authStateManager.setCurrentUser({
          userId: loginResult.userInfo.userId,
          userName: loginResult.userInfo.userName,
          roles: loginResult.userInfo.roles || [],
          permissions: loginResult.userInfo.permissions || [],
          expiresAt: loginResult.expiresAt
        })
      }

      // 更新当前品牌信息
      // 从之前获取的品牌列表中查找对应的品牌信息
      const brands = await getMyBrands()
      const selectedBrand = brands.find(brand => brand.id === brandId)
      if (selectedBrand) {
        brandStore.setCurrentBrand(selectedBrand)
      } else {
        // 如果找不到品牌信息，创建一个基本的品牌对象
        brandStore.setCurrentBrand({ 
          id: brandId, 
          name: `品牌${brandId}`, 
          brandCode: `BRAND${brandId}`,
          logo: ''
        })
      }
      
      // 确保token定时刷新检查已启动（如果之前没有启动）
      startTokenRefreshTimer()
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 发送短信验证码
  const sendSmsCode = async (phoneNumber: string): Promise<boolean> => {
    try {
      const response = await authApi.sendSmsCode({ phoneNumber })
      
      if (!response.success) {
        throw new Error(response.message || '发送验证码失败')
      }

      return true
    } catch (err: any) {
      error.value = err.message
      throw err
    }
  }

  // 登出方法
  const logout = async (): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      await authUtils.handleLogout()
    } catch (err: any) {
      error.value = err.message
      console.error('登出失败:', err)
    } finally {
      loading.value = false
    }
  }

  // 获取用户信息
  const getProfile = async (): Promise<JwtUserInfo | null> => {
    const result = await userStore.fetchUserInfo()
    return result || null
  }

  // 手动获取并输出用户信息（用于调试）
  const fetchAndLogUserInfo = async () => {
    const result = await userStore.refreshUserInfo()
    return result || null
  }

  // 清除错误
  const clearError = () => {
    error.value = null
  }

  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),
    currentUser: userStore.currentUser,
    isLoggedIn: userStore.isLoggedIn,
    
    // 计算属性
    userName: userStore.userName,
    userRoles: userStore.userRoles,
    userPermissions: userStore.userPermissions,
    
    // 方法
    login,
    loginWithSms,
    getMyBrands,
    selectBrand,
    switchBrand,
    sendSmsCode,
    logout,
    getProfile,
    fetchAndLogUserInfo,
    clearError,
    initAuthState
  }
}

// 认证守卫Hook
export function useAuthGuard() {
  const userStore = useUserStore()

  // 检查是否需要登录
  const requireAuth = () => {
    if (!userStore.isLoggedIn) {
      // 跳转到登录页
      window.location.href = '/login'
      return false
    }
    return true
  }

  // 检查是否有特定权限
  const requirePermission = (permission: string) => {
    if (!userStore.isLoggedIn) {
      window.location.href = '/login'
      return false
    }
    
    return userStore.hasPermission(permission)
  }

  return {
    requireAuth,
    requirePermission
  }
} 